// GENERATED FILE — do not edit. Source of truth: contracts/. Regenerate: node scripts/sync-contracts.mjs

import { api } from '../apiClient';

import { z } from 'zod';
// Runtime validation: every named type above has a matching `<Name>Schema` zod export — the
// exact schema the backend validates with (story 2-92), so forms can `.parse()`/`.safeParse()`
// against it instead of hand-rolling checks. `.refine()` cross-field rules are preserved
// verbatim; the refine() error MESSAGE is not — the server owns user-facing text.

export type GeoPoint = {
  line1: string;
  line2?: string | null | undefined;
  city: string;
  state: string;
  postcode: string;
  country: string;
  lat?: number | null | undefined;
  lng?: number | null | undefined;
};
export const GeoPointSchema = z.object({
  line1: z.string().min(1),
  line2: z.string().nullable().optional(),
  city: z.string().min(1),
  state: z.string().min(1),
  postcode: z.string().min(1),
  country: z.string().default("Australia"),
  lat: z.number().nullable().optional(),
  lng: z.number().nullable().optional(),
}).strict();

export type Subject = {
  _id: string;
  organizationId: string;
  domainKey: string;
  name: string;
  referenceCode?: string | null | undefined;
  attributes: Record<string, unknown>;
  teamId?: string | null | undefined;
  archivedAt?: string | null | undefined;
  metadata?: Record<string, unknown> | undefined;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lastInspectionAt: string | null;
  nextScheduledAt: string | null;
};
export const SubjectSchema = z.object({
  _id: z.string(),
  organizationId: z.string(),
  domainKey: z.string(),
  name: z.string(),
  referenceCode: z.string().nullable().optional(),
  attributes: z.record(z.unknown()),
  teamId: z.string().nullable().optional(),
  archivedAt: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).optional(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastInspectionAt: z.string().nullable(),
  nextScheduledAt: z.string().nullable(),
}).strict();

export type CreateSubjectBody = {
  domainKey: string;
  name: string;
  referenceCode?: string | null | undefined;
  attributes?: Record<string, unknown> | undefined;
  metadata?: Record<string, unknown> | undefined;
};
export const CreateSubjectBodySchema = z.object({
  domainKey: z.string().min(1),
  name: z.string().min(1, "Name is required"),
  referenceCode: z.string().nullable().optional(),
  attributes: z.record(z.unknown()).default({}),
  metadata: z.record(z.unknown()).optional(),
}).strict();

export type UpdateSubjectBody = {
  name?: string | undefined;
  referenceCode?: string | null | undefined;
  attributes?: Record<string, unknown> | undefined;
  archived?: boolean | undefined;
  metadata?: Record<string, unknown> | undefined;
};
export const UpdateSubjectBodySchema = z.object({
  name: z.string().min(1, "Name is required").optional(),
  referenceCode: z.string().nullable().optional(),
  attributes: z.record(z.unknown()).optional(),
  archived: z.boolean().optional(),
  metadata: z.record(z.unknown()).optional(),
}).strict().refine((body) => Object.keys(body).length > 0);

export type ListSubjectsResponse = {
  items: Array<Subject>;
  nextCursor: string | null;
  total?: number | undefined;
};

export const ListSubjectsQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  sort: z.string().min(1).optional(),
  q: z.string().min(1).optional(),
  domain: z.string().min(1).optional(),
  status: z.enum(["active", "archived", "all"] as const).default("active"),
});

export async function listSubjects(query?: {
  cursor?: string | undefined;
  limit?: number | undefined;
  sort?: string | undefined;
  q?: string | undefined;
  domain?: string | undefined;
  status?: "active" | "archived" | "all" | undefined;
}): Promise<ListSubjectsResponse> {
  const response = await api.get<ListSubjectsResponse>(`/subjects`, { params: query });
  return response.data;
}

export type CreateSubjectResponse = {
  data: Subject;
};

export async function createSubject(body: CreateSubjectBody): Promise<CreateSubjectResponse> {
  const response = await api.post<CreateSubjectResponse>(`/subjects`, body);
  return response.data;
}

export type GetSubjectResponse = {
  data: Subject;
};

export const GetSubjectParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function getSubject(id: string): Promise<GetSubjectResponse> {
  const response = await api.get<GetSubjectResponse>(`/subjects/${encodeURIComponent(id)}`);
  return response.data;
}

export type UpdateSubjectResponse = {
  data: Subject;
};

export const UpdateSubjectParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function updateSubject(id: string, body: UpdateSubjectBody): Promise<UpdateSubjectResponse> {
  const response = await api.patch<UpdateSubjectResponse>(`/subjects/${encodeURIComponent(id)}`, body);
  return response.data;
}

export const subjectsKeys = {
  all: ["subjects"] as const,
  lists: () => [...subjectsKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...subjectsKeys.lists(), ...(filters ? [filters] : [])] as const,
  details: () => [...subjectsKeys.all, 'detail'] as const,
  detail: (id: string) => [...subjectsKeys.details(), id] as const,
};
